import { Image, Loader2, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PendingRender, useRenderProgress } from '../contexts/RenderProgressContext';
import { Button } from './ui/button';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';

function formatElapsedTime(startedAt: Date): string {
  const seconds = Math.floor((Date.now() - startedAt.getTime()) / 1000);

  if (seconds < 60) {
    return `${seconds}s ago`;
  }

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) {
    return `${minutes}m ago`;
  }

  const hours = Math.floor(minutes / 60);
  return `${hours}h ${minutes % 60}m ago`;
}

interface RenderItemProps {
  render: PendingRender;
  onNavigate: (quoteId: string) => void;
  onDismiss: (id: string) => void;
}

function RenderItem({ render, onNavigate, onDismiss }: RenderItemProps) {
  const [elapsed, setElapsed] = useState(formatElapsedTime(render.startedAt));

  // Update elapsed time every 5 seconds for processing renders
  useEffect(() => {
    if (render.status !== 'processing') return;

    const interval = setInterval(() => {
      setElapsed(formatElapsedTime(render.startedAt));
    }, 5000);

    return () => clearInterval(interval);
  }, [render.startedAt, render.status]);

  return (
    <div className="flex items-start gap-3 p-2 rounded-md hover:bg-muted/50">
      <div className="flex-shrink-0 w-10 h-10 rounded-md bg-muted flex items-center justify-center overflow-hidden">
        {render.status === 'processing' ? (
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        ) : render.status === 'completed' && render.imageUrl ? (
          <img src={render.imageUrl} alt="" className="w-full h-full object-cover" />
        ) : render.status === 'failed' ? (
          <X className="h-4 w-4 text-destructive" />
        ) : (
          <Image className="h-4 w-4 text-muted-foreground" />
        )}
      </div>

      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{render.roomName}</p>
        <p className="text-xs text-muted-foreground truncate">{render.quoteName}</p>
        <p className="text-xs text-muted-foreground">
          {render.status === 'processing' ? `Started ${elapsed}` :
           render.status === 'completed' ? 'Completed' :
           'Failed'}
        </p>
      </div>

      <div className="flex-shrink-0 flex items-center gap-1">
        {render.status !== 'processing' && (
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0"
            onClick={(e) => {
              e.stopPropagation();
              onDismiss(render.id);
            }}
          >
            <X className="h-3 w-3" />
          </Button>
        )}
        <Button
          variant="ghost"
          size="sm"
          className="h-6 px-2 text-xs"
          onClick={() => onNavigate(render.quoteId)}
        >
          View
        </Button>
      </div>
    </div>
  );
}

export default function RenderProgressIndicator() {
  const { pendingRenders, processingCount, removePendingRender, clearCompletedRenders } = useRenderProgress();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  // Don't render if no pending renders
  if (pendingRenders.length === 0) {
    return null;
  }

  const handleNavigate = (quoteId: string) => {
    setOpen(false);
    navigate(`/quotes/${quoteId}/edit`);
  };

  const processingRenders = pendingRenders.filter(r => r.status === 'processing');
  const completedRenders = pendingRenders.filter(r => r.status !== 'processing');

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="relative gap-2 text-sm font-medium"
        >
          {processingCount > 0 ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Image className="h-4 w-4" />
          )}
          <span className="hidden sm:inline">
            {processingCount > 0
              ? `${processingCount} render${processingCount > 1 ? 's' : ''} in progress`
              : `${pendingRenders.length} render${pendingRenders.length > 1 ? 's' : ''}`
            }
          </span>
          {processingCount > 0 && (
            <span className="sm:hidden absolute -top-1 -right-1 h-4 w-4 rounded-full bg-primary text-[10px] text-primary-foreground flex items-center justify-center">
              {processingCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>

      <PopoverContent className="w-80 p-2" align="end">
        <div className="space-y-2">
          <div className="flex items-center justify-between px-2 py-1">
            <h4 className="text-sm font-semibold">Render Progress</h4>
            {completedRenders.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                className="h-6 px-2 text-xs"
                onClick={clearCompletedRenders}
              >
                Clear completed
              </Button>
            )}
          </div>

          {processingRenders.length > 0 && (
            <div className="space-y-1">
              <p className="px-2 text-xs text-muted-foreground uppercase tracking-wide">
                In Progress
              </p>
              {processingRenders.map((render) => (
                <RenderItem
                  key={render.id}
                  render={render}
                  onNavigate={handleNavigate}
                  onDismiss={removePendingRender}
                />
              ))}
            </div>
          )}

          {completedRenders.length > 0 && (
            <div className="space-y-1">
              <p className="px-2 text-xs text-muted-foreground uppercase tracking-wide">
                Recent
              </p>
              {completedRenders.slice(0, 5).map((render) => (
                <RenderItem
                  key={render.id}
                  render={render}
                  onNavigate={handleNavigate}
                  onDismiss={removePendingRender}
                />
              ))}
            </div>
          )}

          {pendingRenders.length === 0 && (
            <p className="px-2 py-4 text-sm text-muted-foreground text-center">
              No renders in progress
            </p>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
